/**
 * @module CatalogueSidebar
 * @purpose Persistent right-hand sidebar listing every registered platform hypothesis
 *   predicate, independent of this case's evaluation state. Static and read-only —
 *   the future seam for per-hypothesis selection/authoring, not built yet.
 * @depends useHypothesisLibrary, CatalogueEntry, react-i18next
 * @dependedOnBy HypothesesView
 *
 * @rules
 * - Performance: fetches once on mount via useHypothesisLibrary; no caseId-scoped refetch
 * - State: owned entirely by useHypothesisLibrary
 * - Errors: inline error state with a retry button that calls the hook's refetch
 */

import { useTranslation } from 'react-i18next'
import { useHypothesisLibrary } from '../../../../../hooks/use-hypothesis-library'
import { CatalogueEntry } from './CatalogueEntry'

interface CatalogueSidebarProps {
  caseId: string
}

const PANEL_STYLE: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '8px',
  overflow: 'auto',
  maxHeight: '100%',
  flex: '1 1 300px',
  minWidth: '280px',
}

const HEADER_STYLE: React.CSSProperties = {
  padding: '12px',
  borderBottom: '1px solid var(--border)',
}

const TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text)',
}

const SUBTITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  marginTop: '4px',
}

const LOADING_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
  color: 'var(--text-muted)',
  padding: '12px',
}

const ERROR_STYLE: React.CSSProperties = {
  padding: '12px',
}

const ERROR_TITLE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '4px',
}

const ERROR_MESSAGE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.75rem',
  color: 'var(--text-dim)',
  marginBottom: '8px',
}

const RETRY_BUTTON_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  padding: '4px 10px',
  borderRadius: '4px',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
  cursor: 'pointer',
}

const EMPTY_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  fontStyle: 'italic',
  padding: '12px',
}

/**
 * The persistent hypothesis catalogue sidebar.
 *
 * @behaviour Header (title + subtitle) followed by one CatalogueEntry per registered
 *   predicate. Handles loading, error (with retry), and empty states. Its own overflow
 *   scrolls independently of the main results column.
 * @errors Renders an inline alert with a retry button on fetch failure.
 */
export function CatalogueSidebar({ caseId }: CatalogueSidebarProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const { predicates, loading, error, refetch } = useHypothesisLibrary()

  return (
    <aside style={PANEL_STYLE} aria-label={t('reasoning.hypothesis.catalogue.title')} data-case-id={caseId}>
      <div style={HEADER_STYLE}>
        <div style={TITLE_STYLE}>{t('reasoning.hypothesis.catalogue.title')}</div>
        <div style={SUBTITLE_STYLE}>{t('reasoning.hypothesis.catalogue.subtitle')}</div>
      </div>
      {loading && (
        <div role="status" aria-busy={true} style={LOADING_STYLE}>
          {t('reasoning.hypothesis.catalogue.loading')}
        </div>
      )}
      {!loading && error !== null && (
        <div role="alert" style={ERROR_STYLE}>
          <div style={ERROR_TITLE_STYLE}>{t('reasoning.hypothesis.catalogue.errorTitle')}</div>
          <div style={ERROR_MESSAGE_STYLE}>{error}</div>
          <button type="button" onClick={() => { void refetch() }} style={RETRY_BUTTON_STYLE}>
            {t('reasoning.hypothesis.catalogue.retry')}
          </button>
        </div>
      )}
      {!loading && error === null && predicates.length === 0 && (
        <div style={EMPTY_STYLE}>{t('reasoning.hypothesis.catalogue.empty')}</div>
      )}
      {!loading && error === null && predicates.length > 0 && predicates.map((entry) => (
        <CatalogueEntry key={entry.name} entry={entry} />
      ))}
    </aside>
  )
}
